import { useEffect, useState } from "react";
import { AppDataProvider, useAppData } from "./state/AppData";
import { AuthProvider, useAuth } from "./state/AuthContext";
import { useRoute, navigate, type Route } from "./lib/router";
import { Nav } from "./components/Nav";
import { ThemeToggle } from "./components/ThemeToggle";
import { StatusStrip } from "./components/StatusStrip";
import { TimelinePanel } from "./components/TimelinePanel";
import { BackendDownBanner } from "./components/BackendDownBanner";
import { LiveMap } from "./components/LiveMap";
import { PacketFeed, useFeedPackets } from "./components/PacketFeed";
import { FeedBar, FeedTab, useFeedDrawer } from "./components/FeedDrawer";
import { NodeDetailPanel } from "./components/NodeDetailPanel";
import { PacketDetailPanel } from "./components/PacketDetailPanel";
import { LoginScreen } from "./components/LoginScreen";
import { AuthCallback } from "./components/AuthCallback";
import { NodeListPage } from "./pages/NodeListPage";
import { NodeDetailPage } from "./pages/NodeDetailPage";
import { SettingsPage } from "./pages/SettingsPage";
import { MqttPage } from "./pages/MqttPage";
import { AuditPage } from "./pages/AuditPage";
import { MessagesPage } from "./pages/MessagesPage";
import { AskPanel } from "./components/AskPanel";
import { PacketsPage } from "./pages/PacketsPage";
import { DecodePage } from "./pages/DecodePage";
import { TracesPage } from "./pages/TracesPage";
import { HealthPage } from "./pages/HealthPage";
import { HazardsPage } from "./pages/HazardsPage";
import { HazardEventDetailPage } from "./pages/HazardEventDetailPage";

function TopBar({ route, askAvailable, askOpen, onToggleAsk }: { route: Route; askAvailable: boolean; askOpen: boolean; onToggleAsk: () => void }) {
  const { status, user, isAdmin, logout } = useAuth();

  // Nav (lib/definitions.ts's NAV_DESTINATIONS) hands back a small, fixed
  // route vocabulary. Only "map" still scrolls within the live view (the
  // map genuinely lives there); "feed" and "status" used to do the same
  // anchor-scroll — a real gap found during review: #/packets and
  // #/health were advertised in the nav but had no route at all, so both
  // silently fell through to the live view's default. They're now real
  // pages like everything else below.
  function onSelectDestination(route_: string) {
    if (route_ === "nodes") {
      navigate("#/nodes");
      return;
    }
    if (route_ === "settings") {
      navigate("#/settings");
      return;
    }
    if (route_ === "mqtt") {
      navigate("#/mqtt");
      return;
    }
    if (route_ === "audit") {
      navigate("#/analysis");
      return;
    }
    if (route_ === "messages") {
      navigate("#/messages");
      return;
    }
    if (route_ === "feed") {
      navigate("#/packets");
      return;
    }
    if (route_ === "status") {
      navigate("#/health");
      return;
    }
    if (route_ === "hazards") {
      navigate("#/hazards");
      return;
    }
    if (route.name !== "live") navigate("#/");
    window.setTimeout(() => {
      document.querySelector(`[data-anchor='map']`)?.scrollIntoView({ behavior: "smooth" });
    }, 0);
  }

  return (
    <div className="topbar">
      <div className="brand" style={{ cursor: "pointer" }} onClick={() => navigate("#/")}>
        AMALGAMESH
      </div>
      <Nav onSelectDestination={onSelectDestination} />
      <div className="spacer" />
      {askAvailable && (
        <button className="sign-out-btn" onClick={onToggleAsk}>
          {askOpen ? "Close Ask" : "Ask"}
        </button>
      )}
      <ThemeToggle />
      {isAdmin && (
        <button className="sign-out-btn topbar-settings" onClick={() => navigate("#/settings")}>
          Settings
        </button>
      )}
      {status === "authenticated" && user && (
        <div className="account-chip">
          <span>{user.label || "signed in"}</span>
          <span className="account-role">{user.role}</span>
          <button className="sign-out-btn" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function LiveView() {
  const { selection, select } = useAppData();
  const { visible, replay } = useFeedPackets();
  const feed = useFeedDrawer(selection !== null);
  const count = replay.loading ? null : visible.length;
  return (
    <>
      <div data-anchor="status">
        <StatusStrip />
      </div>
      <TimelinePanel />
      <div className="main-area" data-feed={feed.collapsed ? "collapsed" : "open"}>
        <div data-anchor="map" style={{ position: "relative", minHeight: 0, height: "100%" }}>
          <BackendDownBanner />
          <LiveMap />
        </div>
        <aside className="side-pane" aria-label="Live packets">
          {/* Folded on a wide screen: a narrow tab on the right edge. On a phone this column is a bottom sheet and
              the bar below is its handle (CSS 01-shell-map.css shows the one that fits the layout). */}
          {feed.collapsed && <FeedTab count={count} onOpen={() => feed.setCollapsed(false)} />}
          <FeedBar count={count} collapsed={feed.collapsed} onSet={feed.setCollapsed} />
          <div className="side-pane-body">
          {selection?.kind === "node" && (
            <div className="panel-block" style={{ maxHeight: "45%" }}>
              <NodeDetailPanel id={selection.id} onClose={() => select(null)} />
            </div>
          )}
          {selection?.kind === "packet" && (
            <div className="panel-block" style={{ maxHeight: "45%" }}>
              <PacketDetailPanel packet={selection.packet} onClose={() => select(null)} />
            </div>
          )}
          <div data-anchor="feed" className="live-feed-anchor">
            <PacketFeed onCollapse={() => feed.setCollapsed(true)} />
          </div>
          </div>
        </aside>
      </div>
    </>
  );
}

// Any route other than the live view renders its own full page but still
// needs somewhere to show a selected packet/node (clicking a packet from
// the Node Detail page, for instance) — a floating drawer rather than
// duplicating the live view's side-pane layout on every page.
function SelectionDrawer() {
  const { selection, select } = useAppData();
  if (!selection) return null;
  return (
    <div className="selection-drawer">
      {selection.kind === "node" && <NodeDetailPanel id={selection.id} onClose={() => select(null)} />}
      {selection.kind === "packet" && <PacketDetailPanel packet={selection.packet} onClose={() => select(null)} />}
    </div>
  );
}

function Shell({ route }: { route: Route }) {
  const { effectiveTheme, features } = useAppData();
  const { isAdmin } = useAuth();
  const [askOpen, setAskOpen] = useState(false);

  // Phase 4's Ask chat (originally scoped alongside audit detectors,
  // coverage-gap and observer-disagreement views, but the chat half never
  // got built). Deliberately NOT a new nav
  // destination (standing rule against pages appearing without
  // discussion): it's a global floating panel, reachable from any route
  // via this one header button, same pattern as SelectionDrawer below
  // (fixed overlay, not tied to a specific page's layout) — "ask about the
  // mesh" is cross-cutting (nodes/packets/telemetry/audit/coverage all at
  // once per its own grounded_on list), not naturally owned by any single
  // existing destination, and the side-panes on Live/Messages are already
  // narrow and busy.
  const askAvailable = features.some((f) => f.id === "ask_chat" && f.enabled);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", effectiveTheme);
  }, [effectiveTheme]);

  // Real topbar height as a CSS variable, kept live via ResizeObserver —
  // found in this build's own QA sweep, 2026-09-20: .selection-drawer and
  // .ask-panel both hardcoded `top: 60px` (the desktop single-row topbar's
  // height), so at phone width, where the topbar wraps to 2-3 rows (up to
  // ~238px on the live route with every control present), both panels
  // started too high and rendered UNDER/overlapping the wrapped nav rather
  // than below it. A single source of truth here fixes both permanently,
  // and any future topbar addition, rather than another hardcoded guess.
  //
  // QA note for whoever writes the next overflow check: a plain
  // `document.body.scrollWidth > window.innerWidth` test (what this build's
  // own sweep script used) only ever catches overflow pushed past the
  // RIGHT edge. Both bugs this effect fixes involved an element pushed off
  // the LEFT edge (a `position:absolute`/`fixed` panel anchored to a
  // trigger that had moved once the topbar wrapped) — scrollWidth doesn't
  // grow for that, so the automated check passed while the page was
  // visibly broken. Check the element's own `getBoundingClientRect().left`
  // (or `.right` against `innerWidth`) too, not just document scrollWidth,
  // for anything positioned outside normal flow.
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(".topbar");
    if (!el) return;
    const apply = () => document.documentElement.style.setProperty("--topbar-h", `${el.getBoundingClientRect().height}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
    // ResizeObserver already reacts to any height change on the topbar
    // element itself (route-driven content changes included) — no need to
    // re-run this effect per route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app-shell">
      <TopBar route={route} askAvailable={askAvailable} askOpen={askOpen} onToggleAsk={() => setAskOpen((o) => !o)} />
      <div className="page-body">
        {route.name === "live" && <LiveView />}
        {route.name === "messages" && <MessagesPage />}
        {route.name === "nodes" && (
          <div className="page-scroll">
            <NodeListPage presetFilter={route.presetFilter} />
          </div>
        )}
        {route.name === "node" && (
          <div className="page-scroll">
            <NodeDetailPage id={route.id} />
          </div>
        )}
        {route.name === "settings" && (
          <div className="page-scroll">
            {/* Frontend-side guard in addition to the backend's own role
                enforcement (every route now requires an explicit role,
                per the coordinator's report) — never offer a control the
                backend will refuse, per the standing rule. */}
            {isAdmin ? <SettingsPage presetCategory={route.presetCategory} presetGroup={route.presetGroup} /> : <div className="empty-state">Settings is admin-only.</div>}
          </div>
        )}
        {route.name === "mqtt" && (
          <div className="page-scroll">
            <MqttPage />
          </div>
        )}
        {route.name === "audit" && (
          <div className="page-scroll">
            <AuditPage presetDetector={route.presetDetector} presetSubmenu={route.presetSubmenu} presetProtocol={route.presetProtocol} />
          </div>
        )}
        {route.name === "packets" && (
          <div className="page-scroll">
            {route.view === "decode" ? <DecodePage /> : route.view === "traces" ? <TracesPage presetTag={route.presetTag} presetWindow={route.presetWindow} /> : <PacketsPage />}
          </div>
        )}
        {route.name === "health" && (
          <div className="page-scroll">
            <HealthPage />
          </div>
        )}
        {route.name === "hazards" && (
          <div className="page-scroll">
            <HazardsPage presetTab={route.presetTab} />
          </div>
        )}
        {route.name === "hazard-event" && (
          <div className="page-scroll">
            <HazardEventDetailPage id={route.id} />
          </div>
        )}
        {route.name !== "live" && <SelectionDrawer />}
      </div>
      {askOpen && <AskPanel onClose={() => setAskOpen(false)} />}
    </div>
  );
}

function SplashScreen() {
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">AMALGAMESH</div>
        <p>Loading…</p>
      </div>
    </div>
  );
}

function AuthGate() {
  const route = useRoute();
  const { status } = useAuth();

  if (route.name === "auth-callback") return <AuthCallback search={route.params} />;
  if (status === "checking") return <SplashScreen />;
  if (status === "setup_required") return <LoginScreen setupMode expired={false} />;
  if (status === "unauthenticated" || status === "expired") return <LoginScreen expired={status === "expired"} />;

  return (
    <AppDataProvider>
      <Shell route={route.name === "login" ? { name: "live" } : route} />
    </AppDataProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}
